// A product's Resources (release.js has the rules), as FEHome.dc.html, POYT.dc.html and
// Accelerator.dc.html draw them: the heading, a tab per section with its count, the tab's note (or
// the product's subtitle) on the right, and the items 3 to a row, each with its kind's icon, title,
// line and button. Locked items show with their lock and when they open, under Opening later, or in
// their own tab where the product keeps them there (POYT). Yours for life carries its badge. A video
// plays above the items. The product's terms link sits on the right where it has one
// (catalogue.settings.terms_url: the Accelerator's "The Property Accelerator terms and conditions"),
// and its footnote under the items (settings.resources.footnote). A link to another product in the
// hub shows only to someone who has it open (the Accelerator's masterclass card).
//
//   <ResourcesSection product={productRow} />    inside a ProductDataProvider: the home's section
//   <ResourcesPage product={productRow} />       the /<product>/resources page
//   <ForLifeResources product={productRow} />    the same page once their access has ended: only the
//                                                yours-for-life items (Adam, 3 Oct; no board draws it)
import { useMemo, useState } from 'react'
import { useMember } from '../app/member.jsx'
import { useNow } from '../lib/clock.js'
import { ownedProducts } from '../library/model.js'
import { usePhone } from '../lib/media.js'
import { useProductData } from './ProductData.jsx'
import { resourceTabs, termsLink } from './release.js'
import { forLifeIntro, forLifeEmpty } from './model.js'
import { ResourceButton, VideoPanel } from './ResourceButton.jsx'
import { KindIcon } from './Icons.jsx'
import '../styles/resources.css'

// The hub's product paths, and whether they have each open, from the Library's rows (the same
// rule as the Library's tiles: their own access rows, open now)
export function useOwnedPaths() {
  const { lib } = useMember()
  const nowMs = useNow()
  return useMemo(() => {
    const d = lib?.data
    if (!d) return { productPaths: [], ownsPath: () => false }
    const productPaths = d.products.filter(p => p.path).map(p => p.path)
    const open = new Set([...ownedProducts(d, nowMs).values()].filter(o => o.open && o.product.path).map(o => o.product.path))
    return { productPaths, ownsPath: path => open.has(path) }
  }, [lib?.data, nowMs])
}

export function ResourcesSection({ product, headingLevel = 'h2', empty = null, forLife = false, intro = null }) {
  const { data: all } = useProductData()
  const nowMs = useNow()
  const phone = usePhone()
  const owned = useOwnedPaths()
  // once access has ended, only what's theirs for life: row level security gives a member nothing
  // else, and this keeps it so for a staff member, who reads every placement
  const data = all && forLife ? { ...all, placements: (all.placements || []).filter(pl => pl.yours_for_life) } : all
  const tabs = data ? resourceTabs(data, nowMs, owned) : []
  const [pick, setPick] = useState(null)
  const [playing, setPlaying] = useState(null)
  if (!tabs.length) return empty
  const tab = tabs.find(t => t.key === pick) || tabs[0]
  const settings = (data.product || product).settings?.resources || {}
  // the product's subtitle speaks of everything in it, so not once only what's theirs for life is left
  const note = tab.note || (forLife ? null : settings.subtitle) || null
  const terms = forLife ? null : termsLink(data.product || product)
  const H = headingLevel
  const video = playing ? tab.cards.find(c => c.id === playing) : null

  return (
    <section className="rs" aria-label="Resources">
      <div className="rs-head">
        <div className="rs-head-main">
          <H className="rs-h">Resources</H>
          <div className="rs-tabs" role="tablist" aria-label="Resources">
            {tabs.map(t => (
              <button key={t.key} type="button" role="tab" aria-selected={t.key === tab.key}
                className={t.key === tab.key ? 'rs-tab rs-tab--on' : 'rs-tab'} onClick={() => { setPick(t.key); setPlaying(null) }}>
                {t.name}<span className="rs-tab-count">{t.count}</span>
              </button>
            ))}
          </div>
        </div>
        {(note || terms) && !phone && (
          <span className="rs-side">
            {note && <span className="rs-note">{note}</span>}
            {terms && <a className="rs-terms" href={terms.href} target="_blank" rel="noopener noreferrer">{terms.label}</a>}
          </span>
        )}
      </div>
      {note && phone && <p className="rs-note rs-note--phone">{note}</p>}
      {terms && phone && <a className="rs-terms rs-terms--phone" href={terms.href} target="_blank" rel="noopener noreferrer">{terms.label}</a>}
      {intro && <p className="rs-intro">{intro}</p>}
      {video && <div className="rs-video"><VideoPanel card={video} onClose={() => setPlaying(null)} /></div>}
      <div className="rs-grid" role="tabpanel">
        {tab.cards.map(c => (
          <div key={c.id} className={c.open ? 'rs-card' : 'rs-card rs-card--locked'}>
            <div className="rs-card-top">
              <span className="rs-icon"><KindIcon kind={c.open ? c.kind : 'locked'} /></span>
              <span className="rs-text">
                <span className="rs-title">{c.title}</span>
                {c.meta && <span className={c.isNew ? 'rs-meta rs-meta--new' : 'rs-meta'}>{c.meta}</span>}
                {c.forLife && <span className="rs-life">Yours for life</span>}
              </span>
            </div>
            {c.open
              ? <ResourceButton card={c} className="rs-btn" onVideo={() => setPlaying(playing === c.id ? null : c.id)} />
              : <span className="rs-when">{c.when}</span>}
          </div>
        ))}
      </div>
      {settings.footnote && <p className="rs-foot">{settings.footnote}</p>}
    </section>
  )
}

export function ForLifeResources({ product }) {
  return (
    <ResourcesSection product={product} headingLevel="h1" forLife intro={forLifeIntro(product)}
      empty={<p className="pp-empty">{forLifeEmpty(product)}</p>} />
  )
}

export function ResourcesPage({ product }) {
  return (
    <ResourcesSection product={product} headingLevel="h1"
      empty={<p className="pp-empty">Nothing here yet. Anything Adam adds for {product.name} shows up here.</p>} />
  )
}
